/**
 * GiNode — the custom xyflow node that renders a GI station.
 *
 * Each input port becomes a leading-side handle (visually left on the
 * canvas); each output port a trailing-side handle (visually right).
 * Handle IDs are the port names; xyflow's `onConnect` surfaces them
 * as `sourceHandle` / `targetHandle`, giving the caller everything
 * needed to validate type compatibility.
 *
 * # RTL
 *
 * The xyflow canvas is forced to `dir="ltr"` in
 * `BlueprintBuilder.tsx` — DAG topology is spatial, not textual, and
 * industry flowchart tools (draw.io, Miro, Figma) do not mirror
 * graphs under RTL. This file therefore uses `Position.Left` /
 * `Position.Right` and physical `items-end` confidently, because
 * every instance renders under an LTR subtree.
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';

import { DomainIcon } from '@/components/DomainIcon';
import type { PortDefinition } from '@/api/types';
import { normalizeDomain } from '@/utils/format';
import { cn } from '@/utils/cn';

import type { BlueprintNodeData } from './types';

export function GiNode({ data, selected }: NodeProps) {
  const d = data as unknown as BlueprintNodeData;
  const normalized = normalizeDomain(d.domain);

  return (
    <div
      className={cn(
        'w-60 rounded-lg border bg-surface shadow-subtle',
        'transition-shadow',
        selected ? 'border-spark shadow-raised' : 'border-border',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2 border-b border-border px-3 py-2',
          `bg-domain-${normalized}-soft text-domain-${normalized}`,
        )}
      >
        <DomainIcon domain={normalized} className="h-4 w-4" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{d.name}</p>
          <p className="truncate font-mono text-[10px] text-ink-muted">v{d.version}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 px-3 py-2 text-[11px]">
        <PortList ports={d.inputs} direction="input" />
        <PortList ports={d.outputs} direction="output" />
      </div>
    </div>
  );
}

function PortList({
  ports,
  direction,
}: {
  ports: PortDefinition[];
  direction: 'input' | 'output';
}) {
  const isOutput = direction === 'output';
  return (
    <ul className={cn('flex flex-col gap-1', isOutput ? 'items-end text-end' : '')}>
      {ports.length === 0 ? (
        <li className="text-[10px] italic text-ink-subtle">(none)</li>
      ) : null}
      {ports.map((p, i) => (
        <li
          key={p.name}
          className="relative flex items-center gap-1 text-ink-muted"
          style={isOutput ? { paddingRight: 10 } : { paddingLeft: 10 }}
        >
          <Handle
            id={p.name}
            type={isOutput ? 'source' : 'target'}
            position={isOutput ? Position.Right : Position.Left}
            style={{
              top: 14 + i * 20,
              width: 10,
              height: 10,
              background: 'rgb(var(--c-spark))',
              border: '2px solid rgb(var(--c-surface))',
            }}
          />
          <span className="truncate">
            {p.name}
            <span className="ms-1 text-[10px] text-ink-subtle">
              · {typeof p.data_type === 'string' ? p.data_type : p.data_type.Custom}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
